import React, { useEffect } from 'react';
import styled from 'astroturf/react';

import { Button } from 'js/design_system/Button';
import { Icon } from 'js/design_system/Icon';
import { BodyLink } from 'js/design_system/link/BodyLink';
import routes from 'js/core_pre_webpack/routes';
import $image_url from 'js/utils/image_url';
import { trackClick } from 'js/utils/trackClick';
import isMobile from 'js/utils/isMobile';
import { ImpressionWatcher } from 'js/utils/impressions/ImpressionWatcher';
import { ImpressionEntity } from 'js/utils/impressions/watchImpressions';
import { fsEvent, initializeFullstory } from 'js/fullstory';
import { currentUser } from 'js/utils/user';
import { WorkId } from 'js/liswp/types';
import PlayIcon from 'js/svg/colorless/play_arrow.svg';

import { usePaperToPodcast } from './usePaperToPodcast';

type Props = {
  workId: WorkId;
  workTitle: string;
  showTitle?: boolean;
  impressionSource?: string;
  onsitePodcastPromotion?: boolean;
  onsitePodcastPromotionRevised?: boolean;
  showFeedbackSection?: boolean;
  onPlayerReady?: () => void;
  initialEpisode?: {
    id: number;
    generationStartedAt?: string | null;
    generationCompletedAt?: string | null;
    streamReadyAt?: string | null;
    transcriptCompletedAt?: string | null;
    visibility?: string;
    duration?: number | null;
  } | null;
};

// Store all podcast instances
const podcastInstances = new Map<WorkId, () => Promise<void>>();

export const SimplePaperPodcast: React.FC<Props> & {
  play: (workId?: WorkId) => Promise<void>;
} = ({
  workId,
  workTitle,
  showTitle = true,
  impressionSource = 'paper_to_podcast_profile',
  onsitePodcastPromotion = false,
  onsitePodcastPromotionRevised = false,
  showFeedbackSection = true,
  onPlayerReady,
  initialEpisode,
}) => {
  const {
    showPlayer,
    feedbackInput,
    loading,
    feedbackSubmitted,
    audioRef,
    setFeedbackInput,
    handlePlay,
    handlePause,
    onSubmitFeedback,
    isReady,
    hasError,
    isPremiumUser,
  } = usePaperToPodcast({ workId, impressionSource, initialEpisode });

  // Store this instance's handlePlay function
  React.useEffect(() => {
    podcastInstances.set(workId, handlePlay);

    // Cleanup when component unmounts
    return () => {
      podcastInstances.delete(workId);
    };
  }, [workId, handlePlay]);

  useEffect(() => {
    if (onsitePodcastPromotion && !currentUser?.admin) {
      initializeFullstory(currentUser ?? undefined);
      fsEvent('OnsitePodcastPromotion');
    }
  }, [onsitePodcastPromotion]);

  // Trigger onPlayerReady callback when the player becomes ready
  useEffect(() => {
    if (isReady && onPlayerReady) {
      onPlayerReady();
    }
  }, [isReady, onPlayerReady]);

  if (hasError) {
    return (
      <Container>
        <ErrorContent>
          <Icon name="error_outline" size={16} />
          <span>Unable to load podcast</span>
        </ErrorContent>
      </Container>
    );
  }

  return (
    <ImpressionWatcher
      style={{ width: '100%' }}
      source={`${impressionSource}.player`}
      entity_id={workId}
      entity_type={ImpressionEntity.Work}
      noBatch
      extra={{ workId }}
      onClick={(e) => e.preventDefault()}
    >
      <Container>
        <ClickableArea
          className="podcast-spotlight-target"
          onClick={loading ? undefined : handlePlay}
          style={{
            display: showPlayer ? 'none' : 'block',
            cursor: loading ? 'default' : 'pointer',
            opacity: loading ? 0.7 : 1,
          }}
        >
          <ContentSection>
            {showTitle && (
              <TitleContainer>
                <WorkTitle>{workTitle}</WorkTitle>
              </TitleContainer>
            )}

            <PlaySection>
              {loading ? (
                <LoadingRow>
                  <LoadingButtonContainer>
                    <LoadingSpinner
                      src={$image_url('value_props/premium-loading.gif')}
                    />
                  </LoadingButtonContainer>
                  <LoadingContent>
                    <LoadingText>Generating podcast...</LoadingText>
                    <LoadingSubtext>This will take a moment</LoadingSubtext>
                  </LoadingContent>
                </LoadingRow>
              ) : (
                <PlayRow>
                  <PlayButton>
                    <PlayIcon size={16} />
                  </PlayButton>

                  <PlayInfo>
                    <PlayLabel>Listen to Podcast Summary</PlayLabel>
                    <PlayMeta>Generated with AI • ~5 min</PlayMeta>
                  </PlayInfo>

                  <HeadphonesIcon>
                    <Icon name="headphones" size={16} />
                  </HeadphonesIcon>
                </PlayRow>
              )}
            </PlaySection>
          </ContentSection>
        </ClickableArea>

        <div style={{ display: showPlayer ? 'block' : 'none' }}>
          <ContentSection>
            {showTitle && (
              <TitleContainer>
                <WorkTitle>{workTitle}</WorkTitle>
              </TitleContainer>
            )}

            <PlayerWithButtonContainer>
              <PlayerContainer>
                <AudioPlayerWrapper>
                  <AudioPlayer
                    ref={audioRef}
                    controls
                    src="placeholder"
                    preload="none"
                    onPlay={handlePlay}
                    onPause={handlePause}
                    onLoadedMetadata={() => {
                      if (audioRef.current) {
                        audioRef.current.currentTime = 0;
                      }
                    }}
                    style={{ width: '100%' }}
                  >
                    Your browser does not support the audio element.
                  </AudioPlayer>
                </AudioPlayerWrapper>
              </PlayerContainer>
              {onsitePodcastPromotion && (
                <PublishButton
                  onClick={() => {
                    trackClick({
                      source: `${impressionSource}.publish_button`,
                      extra: {
                        work_id: workId,
                      },
                    });
                    if (onsitePodcastPromotionRevised) {
                      const upgradeUrl = isMobile()
                        ? '/upgrade?feature=ai_podcast&trigger=work_edit_promotion'
                        : '/upgrade?feature=ai_podcast&referrer=work_edit&trigger=work_edit_promotion';
                      window.location.href = upgradeUrl;
                    } else {
                      window.location.href =
                        '/upgrade?feature=ai_podcast&trigger=own_profile_promotion';
                    }
                  }}
                >
                  {onsitePodcastPromotionRevised
                    ? 'Publish podcast'
                    : 'Publish to Academia'}
                </PublishButton>
              )}
            </PlayerWithButtonContainer>
          </ContentSection>

          {showFeedbackSection && !onsitePodcastPromotion && (
            <BottomSection>
              {!feedbackSubmitted ? (
                <FeedbackRow>
                  <FeedbackInput
                    value={feedbackInput}
                    onChange={(e) => setFeedbackInput(e.target.value)}
                    placeholder="Share feedback..."
                  />
                  <FeedbackButton onClick={onSubmitFeedback} size="sm">
                    Send
                  </FeedbackButton>
                </FeedbackRow>
              ) : (
                <FeedbackSuccess>
                  <Icon name="check" size={14} />
                  <span>Thanks for the feedback!</span>
                </FeedbackSuccess>
              )}

              {!isPremiumUser && (
                <UpgradeLink>
                  <BodyLink
                    onClick={() => {
                      trackClick({
                        source: `${impressionSource}.upgrade_link`,
                        extra: {
                          work_id: workId,
                        },
                      });
                    }}
                    href={routes.upgrade_url({
                      feature: 'ai_podcast',
                      trigger: impressionSource,
                    })}
                  >
                    Create podcasts for your papers →
                  </BodyLink>
                </UpgradeLink>
              )}
            </BottomSection>
          )}
        </div>
      </Container>
    </ImpressionWatcher>
  );
};

// Method that allows external elements to trigger the play button
SimplePaperPodcast.play = async (workId?: WorkId) => {
  if (workId && podcastInstances.has(workId)) {
    // Play specific podcast by workId
    const handlePlay = podcastInstances.get(workId)!;
    await handlePlay();
  } else if (podcastInstances.size === 1) {
    // If only one podcast, play it
    const entryIterator = podcastInstances.entries().next();
    if (!entryIterator.done) {
      const [, handlePlay] = entryIterator.value;
      await handlePlay();
    }
  }
};

const FeedbackButton = styled(Button)``;

const Container = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.2s ease;

  &:hover {
    border-color: #d1d5db;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  ${FeedbackButton} {
    height: 34px;
  }
`;

const ClickableArea = styled.div`
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background: #fafbfc;
  }
`;

const ContentSection = styled.div`
  padding: 16px;
`;

const TitleContainer = styled.div`
  margin-bottom: 12px;
`;

const WorkTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #111827;
  margin: 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const PlayerWithButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
  }
`;

const PlayerContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  @media (min-width: 768px) {
    flex: 1;
  }
`;

const AudioPlayerWrapper = styled.div``;

const AudioPlayer = styled.audio`
  width: 100%;
  height: 40px;
  margin: 0;
  padding: 0;
  vertical-align: middle;
`;

const PlaySection = styled.div`
  min-height: 40px;
  display: flex;
  align-items: center;
`;

const LoadingRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
`;

const LoadingButtonContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
`;

const LoadingSpinner = styled.img`
  height: 16px;
  width: 16px;
`;

const LoadingContent = styled.div``;

const LoadingText = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #374151;
`;

const LoadingSubtext = styled.div`
  font-size: 12px;
  color: #9ca3af;
  margin-top: 2px;
`;

const PlayRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
`;

const PlayButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: #0645b1;
  border-radius: 50%;
  color: white;
  flex-shrink: 0;
  padding-left: 4px;
  transition: all 0.15s ease;

  &:hover {
    background: #2563eb;
    border-color: #2563eb;
  }
`;

const PlayInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const PlayLabel = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 2px;
`;

const PlayMeta = styled.div`
  font-size: 12px;
  color: #9ca3af;
`;

const HeadphonesIcon = styled.div`
  color: #d1d5db;
  flex-shrink: 0;
`;

const BottomSection = styled.div`
  border-top: 1px solid #f3f4f6;
  background: #fafbfc;
`;

const FeedbackRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  padding: 10px 16px;
`;

const FeedbackInput = styled.input`
  flex: 1;
  padding: 6px 10px;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  font-size: 13px;
  background: white;

  &:focus {
    outline: none;
    border-color: #9ca3af;
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const FeedbackSuccess = styled.div`
  padding: 10px 16px;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #059669;
  font-weight: 500;
`;

const UpgradeLink = styled.div`
  padding: 6px 16px 8px 16px;
  border-top: 1px solid #f3f4f6;

  a {
    font-size: 12px;
    color: #6b7280;
    text-decoration: none;
    font-weight: 400;

    &:hover {
      color: #374151;
      text-decoration: underline;
    }
  }
`;

const ErrorContent = styled.div`
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  color: #dc2626;
  font-size: 14px;
`;

const PublishButton = styled.button`
  padding: 8px 16px;
  background: #0645b1;
  color: white;
  border: none;
  border-radius: 12px;
  font-family: 'DM Sans', sans-serif;
  font-size: 13px;
  font-weight: normal;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.15s ease;
  height: 40px;
  flex-shrink: 0;

  &:hover {
    background: #2563eb;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(6, 69, 177, 0.2);
  }

  &:active {
    transform: translateY(1px);
  }
`;
